<template>
  <nav>
    <AlertMessages
      v-if="alertValue"
      :alert-type="alertType"
      :alert-msg="alertMsg"
      @reset="alertValue = false"
    />
    <v-overlay
      data-testid="overlay-screen"
      :value="overlay"
      :absolute="absolute"
      :opacity="opacity"
      :dark="false"
      class="my-overlay text-center"
      color="background"
    >
      <v-tooltip
        bottom
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            data-testid="overlay-signout"
            class="exit-btn mx-md-3  greytext--text"
            absolute
            top
            right
            elevation="0"
            v-bind="attrs"
            @click="signOut"
            v-on="on"
          >
            {{ $t('signOut') }}
            <v-icon class="pl-3">
              mdi-exit-to-app
            </v-icon>
          </v-btn>
        </template>
        <span>{{ $t('signOut') }}</span>
      </v-tooltip>

      <v-card-title class="text-h5 font-weight-bold text-center justify-center mt-16 mb-4 no-break">
        {{ $t('verificationWaitingTitle') }}
      </v-card-title>
      <v-card-subtitle class=" text-center justify-center mb-16 darktext--text text-subtitle-1 no-break">
        {{ $t('verificationWaitingSubTitle') }}
      </v-card-subtitle>

      <v-spacer />
      <v-progress-circular
        :size="125"
        :width="7"
        indeterminate
        class="my-16"
        color="accent"
      />
      <v-spacer />

      <v-card-text class="mt-16 body-2 greytext--text ">
        {{ $t('didntGetEmail') }}    <br>
        {{ $t('checkSpamFolderOr') }}  
        <router-link
          data-testid="overlay-resend-link"
          to="#"
          @click.native="resendConfirmationLink"
        >
          {{ $t('sendAgain') }}
        </router-link>
        <v-progress-circular
          v-if="isLoading"
          size="20"
          indeterminate
          class="mx-2"
        />
      </v-card-text>
    </v-overlay>

    <v-row
      class="fill-height"
      align="center"
    >
      <v-col cols="12" md="7" class="fill-height d-flex flex-column justify-center align-center" style="position:relative">
        <v-toolbar
          class="pa-0"
          dense
          flat
          color="background"
          absolute
          width="100%"
        >
          <v-btn
            v-if="step > 1 && !googleCallback"
            ref="back"
            text
            data-testid="back-button"
            class="rounded-xxl text-none pa-0 mt-4 ml-3"
            :ripple="false"
            color="greytext"
            @click="step--"
          >
            <v-icon>mdi-chevron-left</v-icon>{{ $t('back') }}
          </v-btn>
          <v-spacer />
          <LanguageSwitcher @languageSelected = "updateLocale"/>
        </v-toolbar>
        <v-card max-width="580px" width="100%" elevation="0" class="mx-auto mt-9">
          <v-stepper v-model="step" class="card_background_grey elevation-0">
            <v-stepper-items>
              <v-stepper-content step="1">
                <div class="mb-10">
                  <p class="text-h4 font-weight-bold no-break text-center mb-6">{{ $t('gettingStartedStep1Title') }}</p>
                  <p class="text-body-1 no-break text-center mb-1">{{ $t('gettingStartedStep1Text') }}</p>
                  <p class="text-body-2 no-break text-center font-weight-bold mb-14">{{ $t('gettingStartedStep1Text2') }}</p>
                  <GoogleButton data-testid="google-button"/>
                  <v-btn
                    data-testid="continue-with-email-button"
                    block
                    large
                    class="rounded-xxl text-none mt-6"
                    color="accent"
                    @click="goToNextStep(2)"
                  >
                   {{ $t('continueWithEmailButton') }}
                  </v-btn>
                </div>
              </v-stepper-content>
              <v-stepper-content step="2">
                <div class="mb-10">
                  <p class="text-h5 font-weight-bold no-break text-center">{{ $t('gettingStartedStep2Title') }}</p>
                  <p class="text-body-2 no-break text-center">{{ $t('gettingStartedStep2Text') }}</p>
                </div>
                <v-form
                  ref="2"
                  v-model="valid"
                  @submit.prevent
                >
                  <v-row no-gutters>
                    <v-col cols="12" md="6" class="pr-md-2">
                      <p class="text-body-2 greytext--text no-break mb-1">
                        {{ $t('labelFirstName') }}
                      </p>
                      <v-text-field
                        v-model="firstName"
                        data-testid="first-name-input"
                        solo
                        hide-details="auto"
                        class="rounded-xxl text-body-2 mb-2"
                        :placeholder="$t('createAccountFirstNamePlaceholder')"
                        :rules="firstNameRules"
                        @keyup.enter="focusNext($event.target)"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="pl-md-2">
                      <p class="text-body-2 greytext--text no-break mb-1">
                        {{ $t('labelLastName') }}
                      </p>
                      <v-text-field
                        v-model="lastName"
                        data-testid="last-name-input"
                        solo
                        hide-details="auto"
                        class="rounded-xxl text-body-2 mb-2"
                        :placeholder="$t('createAccountLastNamePlaceholder')"
                        :rules="lastNameRules"
                        @keyup.enter="focusNext($event.target)"
                      />
                    </v-col>
                  </v-row>
                  <p class="text-body-2 greytext--text no-break mb-1">
                    {{ $t('labelEmail') }}
                  </p>
                  <v-text-field
                    v-model="email"
                    data-testid="email-input"
                    solo
                    hide-details="auto"
                    class="rounded-xxl text-body-2 mb-2"
                    :placeholder="$t('createAccountEmailPlaceholder')"
                    :rules="emailRules"
                    @keyup.enter="focusNext($event.target)"
                  />
                  <p class="text-body-2 greytext--text no-break mb-1">
                    {{ $t('labelConfirmEmail') }}
                  </p>
                  <v-text-field
                    v-model="confirmEmail"
                    data-testid="email-confirm-input"
                    solo
                    hide-details="auto"
                    class="rounded-xxl text-body-2 mb-2"
                    :placeholder="$t('createAccountConfirmEmailPlaceholder')"
                    :rules="emailRules.concat(emailConfirmationRule)"
                    @keyup.enter="focusNext($event.target)"
                  />
                  <v-btn
                    data-testid="next-button"
                    block
                    class="rounded-xxl text-none mt-6"
                    color="accent"
                    @click="goToNextStep(3)"
                    type="submit"
                  >
                    {{ $t('next') }}
                  </v-btn>
                  <div class="text-center text-decoration-none text-body-2 greytext--text mt-6"> 
                    <router-link
                      to="/login"
                      class="text-decoration-none text-body-2 darktext--text"
                    >
                      {{ $t('createAccountLoginWithExistingAccount') }}
                    </router-link>
                  </div>
                </v-form>
              </v-stepper-content>
              <v-stepper-content step="3">
                <div class="mb-10">
                  <p class="text-h5 font-weight-bold no-break text-center">{{ $t('gettingStartedStep3Title') }}</p>
                  <p class="text-body-2 no-break text-center">{{ $t('gettingStartedStep3Text') }}</p>
                </div>
                <v-form
                  ref="3"
                  v-model="valid"
                  @submit.prevent
                >
                  <p class="text-body-2 greytext--text no-break mb-1">
                    {{ $t('labelPassword') }}
                  </p>
                  <v-text-field
                    v-model="password"
                    data-testid="password-input"
                    solo
                    hide-details="auto"
                    class="rounded-xxl text-body-2 mb-2"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showPassword ? 'text' : 'password'"
                    :placeholder="$t('createAccountPasswordPlaceholder')"
                    :rules="passwordRules"
                    @click:append="showPassword = !showPassword"
                    @keyup.enter="focusNextNext($event.target)"
                  />
                  <p class="text-body-2 greytext--text no-break mb-1">
                    {{ $t('labelPassword') }}
                  </p>
                  <v-text-field
                    v-model="confirmPassword"
                    data-testid="password-confirm-input"
                    solo
                    hide-details="auto"
                    class="rounded-xxl text-body-2 mb-2"
                    :append-icon="showPasswordConfirm ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showPasswordConfirm ? 'text' : 'password'"
                    :placeholder="$t('createAccountConfirmPasswordPlaceholder')"
                    :rules="passwordRules.concat(passwordConfirmationRule)"
                    @click:append="showPasswordConfirm = !showPasswordConfirm"
                    @keyup.enter="focusNextNext($event.target)"
                  />
                  <v-btn
                    data-testid="next-button"
                    block
                    class="rounded-xxl text-none mt-16"
                    color="accent"
                    @click="goToNextStep(4)"
                    type="submit"
                  >
                    {{ $t('next') }}
                  </v-btn>
                </v-form>
              </v-stepper-content>
              <v-stepper-content step="4">
                <div class="mb-10">
                  <p class="text-h5 font-weight-bold no-break text-center">{{ $t('gettingStartedStep4Title') }}</p>
                  <p class="text-body-2 no-break text-center">{{ $t('gettingStartedStep4Text') }}</p>
                </div>
                <v-form  ref="form"
                  v-model="valid"
                  @submit.prevent
                  class="checkbox-align-start"
                >
                  <p class="text-body-2 greytext--text no-break mb-1">
                  {{ $t('labelCompanyName') }}
                  </p>
                  <v-text-field
                    v-model="companyName"
                    data-testid="company-name-input"
                    solo
                    hide-details="auto"
                    class="rounded-xxl text-body-2 mb-2"
                    :placeholder="$t('createAccountCompanyNamePlaceholder')"
                    :rules="companyNameRules"
                    @keyup.enter="focusNext($event.target)"
                    :error-messages="errorsName"
                  />
                  <p class="text-body-2 greytext--text no-break mb-1">
                    {{ $t('labelSubdomain') }}
                  </p>
                  <v-text-field
                    v-model="subdomain"
                    data-testid="subdomain-input"
                    solo
                    hide-details="auto"
                    class="rounded-xxl text-body-2 mb-2"
                    :placeholder="$t('createAccountSubdomainPlaceholder')"
                    :rules="subdomainRules"
                    @keyup.enter="focusNext($event.target)"
                    :error-messages="errorsSubdomain"
                    :suffix="'.'+domain"
                  />
                  <v-checkbox
                    v-model="acceptedGdpr"
                    data-testid="gdpr-input"
                    color="accent"
                    @change="gdpr($event)"
                    class="pa-2"
                    hide-details
                  >
                    <template v-slot:label>
                      <!-- eslint-disable vue/no-v-html -->
                      <div
                      class="text-body-2 greytext--text"
                        ref="link"
                        v-html="$t('checkboxGdprLabel', {
                          'terms-of-services': `<a target='_blank' href='/terms-of-service'>${ $t('termsOfServices') }</a>`,
                          'privacy-policy': `<a target='_blank' href='/privacy-policy'>${ $t('privacyPolicy') }</a>`
                        })" 
                      />
                    </template>
                  </v-checkbox>
                  <v-btn
                    data-testid="submit-button"
                    block
                    class="rounded-xxl text-none"
                    color="accent"
                    @click="register"
                    :disabled="buttonDisabled"
                  >
                    {{ $t('signUp') }}
                  </v-btn>
                </v-form>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card>
      </v-col>
      <v-col cols="5" class="getting-started-bg d-none d-md-block">
      </v-col>
    </v-row>
  </nav>
</template>
<style scoped>
  .my-overlay :deep() .v-overlay__content {
    width: 100%;
    height: 100%;
  }
  .exit-btn{
    text-transform: none;
    font-weight: normal;
    font-size: 16px;
  }
</style>
<script>
import { ruleRequired, ruleValidMin2Max70Characters, emailRuleValid, passwordRuleValid, passwordRuleCharactersValid, ruleValidMin2Characters, subdomainLRuleCharactersValid } from '../../rules/formRules.js';
import i18n from '../../plugins/i18n'
import { registerUserGettingStarted, resendEmail, redirectUserAfterSocialLogin } from '../../api/userApi'
import { checkSpace } from '../../api/spaceApi'
import { repeatedMethodsMixin } from '../../mixins/repeatedMethodsMixin';
import LanguageSwitcher from '../../components/LanguageSwitcher'
import {loginAuth, logoutAuth } from '../../auth/auth.js'
import AlertMessages  from '../../components/AlertMessages'
import GoogleButton from '../../components/GoogleButton'

import Echo from "laravel-echo"
window.Pusher = require('pusher-js')
export default {
    components: {
        LanguageSwitcher,
        AlertMessages,
        GoogleButton
    },
    mixins: [repeatedMethodsMixin],
    data(){
        return{
            valid: true,
            email: '',
            confirmEmail: '',
            firstName: '',
            lastName: '',
            password: '',
            confirmPassword: '',
            acceptedGdpr: undefined,
            showPassword: false,
            showPasswordConfirm: false,
            alertType: '',
            alertMsg: '',
            alertValue: false,
            buttonDisabled: true,
            token: '',
            isLoading: false,
            overlay: false,
            absolute: true,
            opacity: 1,
            userId: '',
            userObj: {},
            referer: null,
            language: window.$cookies.get('language') ? window.$cookies.get('language') : 'en',
            focusNext(elem) {
                const currentIndex = Array.from(elem.form.elements).indexOf(elem);
                elem.form.elements.item(
                    currentIndex < elem.form.elements.length - 1 ?
                    currentIndex + 1 :
                    0
                ).focus();
            },
            focusNextNext(elem) {
                const currentIndex = Array.from(elem.form.elements).indexOf(elem);
                elem.form.elements.item(
                    currentIndex < elem.form.elements.length - 1 ?
                    currentIndex + 2 :
                    0
                ).focus();
            },
            companyName: '',
            subdomain: '',
            companyNameRules: ruleRequired('labelCompanyName').concat(ruleValidMin2Characters('labelCompanyName')),
            subdomainRules: ruleRequired('labelSubdomain').concat(subdomainLRuleCharactersValid, ruleValidMin2Characters('labelSubdomain')),
            step: 1,
            errorsName: [],
            errorsSubdomain: [],
            domain: process.env.VUE_APP_DOMAIN,
            googleCallback: false,
            loginToken: null,
            languageRuleActive: true
         }
    },
    computed: {
        firstNameRules() {
            return this.languageRuleActive ? ruleRequired('labelFirstName').concat(ruleValidMin2Max70Characters('labelFirstName')) : []
        },
        lastNameRules() {
            return this.languageRuleActive ? ruleRequired('labelLastName').concat(ruleValidMin2Max70Characters('labelLastName')) : []
        },
        emailRules() {
            return this.languageRuleActive ? ruleRequired('labelEmail').concat(emailRuleValid) : []
        },
        passwordRules() {
            return this.languageRuleActive ? ruleRequired('labelPassword').concat(passwordRuleCharactersValid, passwordRuleValid) : []
        },
        passwordConfirmationRule() {
            return () =>
                this.password === this.confirmPassword || i18n.t('fieldErrorMatch', {'field': i18n.t('labelPassword') })
        },
        emailConfirmationRule() {
            return () =>
                this.email === this.confirmEmail || i18n.t('fieldErrorMatch', {'field': i18n.t('labelEmail') })
        }
    },
    watch: {
        '$i18n.locale': function() {
            setTimeout(() => {
                this.$refs['link'].children[0].addEventListener('click', function(event) {
                    event.stopImmediatePropagation()
                })
                this.$refs['link'].children[1].addEventListener('click', function(event) {
                    event.stopImmediatePropagation()
                })
            }, 100)
        },
        async subdomain (val) {
            try {
              let data = {
                  data: {
                      type: "spaces",
                      attributes: {
                          name: this.companyName,
                          subdomain: val
                      }
                  }
              }
              const response = await checkSpace(data)
              if(response.status == 200) {
                  this.errorsName = []
                  this.errorsSubdomain = []
              }
            } catch (e) {
                if(e.response){
                    this.errorsSubdomain = []
                    if (e.response.status == 422 ){
                        for(let i = 0; i < e.response.data.errors.length; i++){
                            if(e.response.data.errors[i].source.pointer == '/data/attributes/subdomain' && e.response.data.errors[i].detail == "The subdomain has already been taken.") {
                                this.errorsSubdomain = [i18n.t('gettingStartedSpaceErrorURLAlreadyTaken')]
                            }
                        }
                    }
                }
            }   
        },
        async companyName (val) {
            try {
              let data = {
                  data: {
                      type: "spaces",
                      attributes: {
                          name: val,
                          subdomain: this.subdomain
                      }
                  }
              }
              const response = await checkSpace(data)
              if(response.status == 200) {
                  this.errorsName = []
                  this.errorsSubdomain = []
              }
            } catch (e) {
                if(e.response){
                    this.errorsName = []
                    if (e.response.status == 422 ){
                        for(let i = 0; i < e.response.data.errors.length; i++){
                            if(e.response.data.errors[i].source.pointer == '/data/attributes/name' && e.response.data.errors[i].detail == "The name has already been taken."){
                                this.errorsName =  [i18n.t('gettingStartedSpaceErrorNameAlreadyTaken')]
                            }
                        }
                    }
                }
            }   
        }
    },
    created() {
        let queryString = window.location.search;
        let urlParams = new URLSearchParams(queryString);
        let referer = urlParams.get('referer')
        if (referer) {
            this.referer = referer;
        }
        if(this.$route.params.loginToken){     
          this.googleCallback = true
          this.loginToken = this.$route.params.loginToken
          this.step = 4
        }
    },
    mounted() {
        //add tag manager scripts
        if(process.env.NODE_ENV == 'production'){
            let tagManagerScriptHead = document.createElement('script')
            tagManagerScriptHead.append(`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','GTM-K3JV3QN');`)
            document.head.prepend(tagManagerScriptHead)

            let tagManagerNoScriptBody = document.createElement('noscript')
            let tagManagerScriptBody = document.createElement('script')
            tagManagerScriptBody.setAttribute('src', 'https://www.googletagmanager.com/ns.html?id=GTM-K3JV3QN')
            tagManagerScriptBody.setAttribute('height', 0)

            tagManagerScriptBody.setAttribute('width', 0)
            tagManagerScriptBody.setAttribute('style','display:none;visibility:hidden')
            tagManagerNoScriptBody.appendChild(tagManagerScriptBody)

            document.body.prepend(tagManagerNoScriptBody)
            //END add tag manager scripts
        }
       
        this.$refs['link'].children[0].addEventListener('click', function(event) {
            event.stopImmediatePropagation()
        })
        this.$refs['link'].children[1].addEventListener('click', function(event) {
            event.stopImmediatePropagation()
        })
    },
    methods: {
        gdpr: function(e) {
            this.buttonDisabled = e === true ? false : true
        },
        async register(){
            this.language = window.$cookies.get('language') ? window.$cookies.get('language') : 'en'
            this.$refs.form.validate()
            if(this.valid){
                this.buttonDisabled = true
                if(this.googleCallback){
                    let data = null;
                    data = {
                        data: {
                            type: "users", 
                            attributes: {
                                accepted_gdpr: true,
                                language: this.language.trim(),
                                company_name: this.companyName.trim(),
                                subdomain: this.subdomain.trim()
                            }
                        }
                    };
                    const response = await redirectUserAfterSocialLogin(data, this.loginToken)
                    if(response.status == 200) {
                        //redirect to the new space
                        loginAuth(response.data.meta.access_token, response.data.data)
                        i18n.locale = response.data.data.attributes.language && response.data.data.attributes.language.trim()
                        window.$cookies.set("language", i18n.locale, 0, null, process.env.VUE_APP_DOMAIN)
                        window.location.href = process.env.VUE_APP_PROTOCOL + "://" + this.subdomain + '.' + process.env.VUE_APP_DOMAIN
                        
                    }
                }else {
                    try {
                        let data = null;
                        data = {
                            data: {
                                type: "users", 
                                attributes: {
                                    first_name: this.firstName.trim(),
                                    last_name: this.lastName.trim(),
                                    email: this.email.trim(),
                                    email_confirmation: this.confirmEmail.trim(),
                                    password: this.password.trim(),
                                    password_confirmation: this.confirmPassword.trim(),
                                    accepted_gdpr: this.acceptedGdpr,
                                    language: this.language.trim(),
                                    company_name: this.companyName.trim(),
                                    subdomain: this.subdomain.trim()
                                }
                            }
                        };

                        const response = await registerUserGettingStarted(data)
                        if(response.status == 201) {
                            this.overlay = true;
                            this.token = response.data.meta.access_token;
                            this.userId = response.data.data.id;
                            this.userObj = response.data.data;
                            this.listen();
                        }
                        
                    } catch (e) {
                        if(e.response){
                            this.alertValue = true
                            this.alertType = 'error'
                            if(e.response.status == 460){
                                this.alertMsg =  i18n.t('createAccountErrorEmailAlreadyUsed')
                            }
                            else if (e.response.status == 422 ){
                                for(let i = 0; i < e.response.data.errors.length; i++){
                                    if(e.response.data.errors[i].source.pointer == '/data/attributes/company_name' && e.response.data.errors[i].detail == "The company name has already been taken."){
                                        this.alertMsg =  i18n.t('gettingStartedSpaceErrorNameAlreadyTaken')
                                    }
                                    else if(e.response.data.errors[i].source.pointer == '/data/attributes/subdomain' && e.response.data.errors[i].detail == "The subdomain has already been taken.") {
                                        this.alertMsg =  i18n.t('gettingStartedSpaceErrorURLAlreadyTaken')
                                    }
                                    else if(e.response.data.errors[i].detail == "The password must contain at least one special character!") {
                                        this.alertMsg =  i18n.t('resetPasswordInvalidCharacter')
                                    }
                                    else {
                                        this.alertMsg =  i18n.t('serverError')
                                    }
                                }
                            }
                            else {
                                this.alertMsg =  i18n.t('serverError')
                            }
                        }
                    }
                }
                this.buttonDisabled = this.acceptedGdpr === true ? false : true
            }
        },
        async resendConfirmationLink(){
            this.language = window.$cookies.get('language') ? window.$cookies.get('language') : 'en'
            this.isLoading = true
            try {
                const response = await resendEmail(this.token)
                if(response.status == 200) {
                    this.alertValue = true
                    this.alertType = 'success'
                    this.alertMsg =  i18n.t('verificationLinkSent')
                    this.isLoading = false
                }
            } catch (e){
              this.alertValue = true
                this.alertType = 'error'
                this.alertMsg =  i18n.t('serverError')
                this.isLoading = false
            }
        },
        async listen(){
          if (this.token) {
              window.Echo = new Echo({
                  broadcaster: 'pusher',
                  key: process.env.VUE_APP_PUSHER_KEY,
                  cluster: 'eu',
                  encryted: true,
                  authEndpoint: process.env.VUE_APP_API_BASE_URL + "/v1/broadcasting/auth",
                  auth: {
                      headers: {
                          'Accept': "application/vnd.api+json",
                          'Authorization': 'Bearer ' + this.token
                      }
                  },
              });
              window.Echo.private('user-verification-tracker.' + this.userId)
              .listen('UserVerified', () => {
                  this.hideLoader();
              });
          }
        },
        hideLoader() {
          loginAuth(this.token, this.userObj)
          i18n.locale = this.userObj.attributes.language && this.userObj.attributes.language.trim()
          window.$cookies.set("language", i18n.locale, 0, null, process.env.VUE_APP_DOMAIN)

          window.location.href = process.env.VUE_APP_PROTOCOL + "://" + this.subdomain + '.' + process.env.VUE_APP_DOMAIN
        
        },
        signOut(){
          logoutAuth()
          this.$router.push({path: '/login'})
        },
        goToNextStep(step){
          var validStep = true
          if(step > 2){
            validStep = this.$refs[step-1].validate()
          }
          if(validStep){
            this.step = step
          }
        },
        updateLocale() {
            this.languageRuleActive = false
            this.$nextTick(() => {
              this.languageRuleActive = true
            })
        },
      }
    }
</script>
<style>
.getting-started-bg{
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: url( '../../assets/getting-started-bg.png') no-repeat center center;
    background-size: cover;
  }
</style>